import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FileText, Download, Trash2, Save, Send, Copy, Check, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { supabase } from "../lib/supabase";
import { ProjectService } from "../services/projectService";
import { formatDate } from "../utils/dateFormatting";
import { formatTextWithLinks } from "../utils/linkFormatter";
import { toCdnDownloadUrl } from "../utils/fileUtils";
import { Modal, ModalBody, ModalFooter, Input, Dropdown, Button, ConfirmDialog } from './ui';

const ModelQuotesModalNoPricing = ({
  quote,
  clients,
  contacts,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    company_name: "",
    name: "",
    project_name: "",
    client_name: "",
    selectedClientId: "",
    selectedContactId: "",
    quote_due_date: "",
    date_quoted: "",
    additional_requests: [],
    project_info: "",
    notes_from_ab_civil: "",
    quote_status: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Copy files to project state
  const [showCopySection, setShowCopySection] = useState(false);
  const [clientProjects, setClientProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedProjectFiles, setSelectedProjectFiles] = useState([]);
  const [loadingProjectFiles, setLoadingProjectFiles] = useState(false);
  const [selectedFilesToCopy, setSelectedFilesToCopy] = useState({});
  const [copying, setCopying] = useState(false);
  const [copySuccess, setCopySuccess] = useState(null);

  // Filter contacts based on selected client
  const filteredContacts = useMemo(() => {
    if (!formData.selectedClientId || !contacts) return [];
    return contacts.filter((contact) => {
      return (
        String(contact.clientId) === String(formData.selectedClientId) ||
        String(contact.client_id) === String(formData.selectedClientId)
      );
    });
  }, [formData.selectedClientId, contacts]);

  // Prepare dropdown options for clients
  const clientOptions = useMemo(() => {
    if (!clients) return [];
    return clients.map(client => ({
      value: client.id,
      label: client.name
    }));
  }, [clients]);

  // Prepare dropdown options for contacts
  const contactOptions = useMemo(() => {
    if (!filteredContacts) return [];
    return filteredContacts.map(contact => ({
      value: contact.id,
      label: contact.name
    }));
  }, [filteredContacts]);

  // Quote status options
  const quoteStatusOptions = [
    { value: '', label: 'Select status' },
    { value: 'To Quote', label: 'To Quote' },
    { value: 'Quoted', label: 'Quoted' },
    { value: 'Accepted', label: 'Accepted' },
    { value: 'Declined', label: 'Declined' },
  ];

  useEffect(() => {
    if (quote) {
      setFormData({
        company_name: quote.company_name || "",
        name: quote.name || "",
        project_name: quote.project_name || "",
        client_name: quote.client_name || "",
        selectedClientId: quote.client_id || "",
        selectedContactId: quote.contact_id || "",
        quote_due_date: quote.quote_due_date
          ? formatDate(quote.quote_due_date, "input")
          : "",
        date_quoted: quote.date_quoted
          ? formatDate(quote.date_quoted, "input")
          : "",
        additional_requests: quote.additional_requests || [],
        project_info: quote.project_info || "",
        notes_from_ab_civil: quote.notes_from_ab_civil || "",
        quote_status: quote.quote_status || "",
      });

      // If no client_id but has client_name, try to match
      if (!quote.client_id && quote.client_name && clients) {
        const matchedClient = clients.find(
          (c) => c.name?.toLowerCase() === quote.client_name?.toLowerCase(),
        );
        if (matchedClient) {
          setFormData((prev) => ({
            ...prev,
            selectedClientId: matchedClient.id,
          }));
        }
      }
    }
  }, [quote, clients, contacts]);

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };

    // Clear contact when client changes
    if (field === "selectedClientId") {
      newFormData.selectedContactId = "";

      // Update client_name when client is selected
      if (value) {
        const selectedClient = clients.find((c) => c.id === parseInt(value));
        if (selectedClient) {
          newFormData.client_name = selectedClient.name;
        }
      }
    }

    setFormData(newFormData);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const fieldsToUpdate = {
        company_name: formData.company_name || null,
        name: formData.name || null,
        project_name: formData.project_name || null,
        client_name: formData.client_name || null,
        client_id: formData.selectedClientId ? parseInt(formData.selectedClientId) : null,
        contact_id: formData.selectedContactId ? parseInt(formData.selectedContactId) : null,
        quote_due_date: formData.quote_due_date || null,
        date_quoted: formData.date_quoted || null,
        additional_requests:
          formData.additional_requests.length > 0
            ? formData.additional_requests
            : null,
        project_info: formData.project_info || null,
        notes_from_ab_civil: formData.notes_from_ab_civil || null,
        quote_status: formData.quote_status || null,
      };

      await ProjectService.updateModelQuoteBatch(quote.id, fieldsToUpdate);

      if (onSave) {
        await onSave();
      }
      onClose();
    } catch (err) {
      console.error("Error saving model quote:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSend = async () => {
    try {
      setSaving(true);
      setError(null);

      const fieldsToUpdate = {
        company_name: formData.company_name || null,
        name: formData.name || null,
        project_name: formData.project_name || null,
        client_name: formData.client_name || null,
        client_id: formData.selectedClientId ? parseInt(formData.selectedClientId) : null,
        contact_id: formData.selectedContactId ? parseInt(formData.selectedContactId) : null,
        quote_due_date: formData.quote_due_date || null,
        date_quoted: formData.date_quoted || null,
        additional_requests:
          formData.additional_requests.length > 0
            ? formData.additional_requests
            : null,
        project_info: formData.project_info || null,
        notes_from_ab_civil: formData.notes_from_ab_civil || null,
        quote_status: "Quoted", // Always set to Quoted when using Save and Send
      };

      await ProjectService.updateModelQuoteBatch(quote.id, fieldsToUpdate);

      if (onSave) {
        await onSave();
      }
      onClose();
    } catch (err) {
      console.error("Error saving and sending model quote:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFileDownload = (fileUrl, fileName) => {
    const link = document.createElement('a');
    link.href = toCdnDownloadUrl(fileUrl);
    link.download = fileName || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);

      await ProjectService.deleteModelQuote(quote.id);

      if (onSave) {
        await onSave();
      }

      onClose();
    } catch (err) {
      console.error("Error deleting model quote:", err);
      setError(`Failed to delete quote: ${err.message}`);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Fetch projects for the quote's client when copy section is opened
  const fetchClientProjects = useCallback(async () => {
    const clientId = quote?.client_id || formData.selectedClientId;
    if (!clientId) return;

    setLoadingProjects(true);
    try {
      const { data, error } = await supabase
        .from("projects")
        .select("id, name, job_number, rev")
        .eq("client_id", clientId)
        .order("job_number", { ascending: false });

      if (error) throw error;
      setClientProjects(data || []);
    } catch (err) {
      console.error("Error fetching client projects:", err);
      setClientProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, [quote?.client_id, formData.selectedClientId]);

  // Fetch selected project's file_attachments
  const fetchProjectFiles = useCallback(async (projectId) => {
    if (!projectId) {
      setSelectedProjectFiles([]);
      return;
    }

    setLoadingProjectFiles(true);
    try {
      const { data, error } = await supabase
        .from("projects")
        .select("file_attachments")
        .eq("id", projectId)
        .single();

      if (error) throw error;
      setSelectedProjectFiles(data?.file_attachments || []);
    } catch (err) {
      console.error("Error fetching project files:", err);
      setSelectedProjectFiles([]);
    } finally {
      setLoadingProjectFiles(false);
    }
  }, []);

  // Handle toggling the copy section
  const handleToggleCopySection = () => {
    if (!showCopySection) {
      fetchClientProjects();
      // Select all files by default
      const allSelected = {};
      (quote?.file_attachments || []).forEach((_, index) => {
        allSelected[index] = true;
      });
      setSelectedFilesToCopy(allSelected);
    }
    setShowCopySection(!showCopySection);
    setCopySuccess(null);
  };

  // Handle project selection change
  const handleProjectSelect = (projectId) => {
    setSelectedProjectId(projectId);
    setCopySuccess(null);
    if (projectId) {
      fetchProjectFiles(projectId);
    } else {
      setSelectedProjectFiles([]);
    }
  };

  // Toggle individual file selection
  const toggleFileSelection = (index) => {
    setSelectedFilesToCopy(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Select/deselect all files
  const toggleSelectAll = () => {
    const allSelected = Object.values(selectedFilesToCopy).every(v => v);
    const newSelection = {};
    (quote?.file_attachments || []).forEach((_, index) => {
      newSelection[index] = !allSelected;
    });
    setSelectedFilesToCopy(newSelection);
  };

  // Copy selected files to the target project
  const handleCopyFiles = async () => {
    if (!selectedProjectId) return;

    const filesToCopy = (quote?.file_attachments || []).filter((_, index) => selectedFilesToCopy[index]);
    if (filesToCopy.length === 0) return;

    setCopying(true);
    setCopySuccess(null);
    try {
      // Merge with existing project files
      const mergedFiles = [...selectedProjectFiles, ...filesToCopy];

      const { error } = await supabase
        .from("projects")
        .update({
          file_attachments: mergedFiles,
          last_modified_at: new Date().toISOString(),
        })
        .eq("id", selectedProjectId);

      if (error) throw error;

      // Refresh the target project's files to show the updated state
      await fetchProjectFiles(selectedProjectId);
      setCopySuccess(`${filesToCopy.length} file${filesToCopy.length > 1 ? 's' : ''} copied successfully!`);
    } catch (err) {
      console.error("Error copying files to project:", err);
      setError(`Failed to copy files: ${err.message}`);
    } finally {
      setCopying(false);
    }
  };

  // Reset copy state when modal closes or quote changes
  useEffect(() => {
    setShowCopySection(false);
    setSelectedProjectId("");
    setSelectedProjectFiles([]);
    setClientProjects([]);
    setCopySuccess(null);
  }, [quote?.id]);

  const selectedFileCount = Object.values(selectedFilesToCopy).filter(v => v).length;

  if (!isOpen) return null;

  return (
    <>
      <Modal trackAs="ModelQuotesModalNoPricing" isOpen={isOpen} onClose={onClose} size="lg">
        {/* Standard Header */}
        <div
          style={{
            padding: 'var(--space-6)',
            borderBottom: '1px solid var(--gray-700)',
            background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
            borderTopLeftRadius: 'var(--radius-xl)',
            borderTopRightRadius: 'var(--radius-xl)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: 'var(--radius-full)',
                backgroundColor: 'var(--cta-blue)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FileText size={28} color="white" />
            </div>
            <div style={{ flex: 1 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                }}
              >
                Edit Model Quote
              </h1>
              <p
                style={{
                  margin: 0,
                  marginTop: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                }}
              >
                {formData.project_name || 'No project name'}
              </p>
            </div>
          </div>
        </div>

        <ModalBody>
          {error && (
            <div
              style={{
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--danger)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/* Project Information Section */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Project Information
              </h2>

              {/* Project Name */}
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <Input
                  label="Project Name"
                  value={formData.project_name}
                  onChange={(e) => handleInputChange("project_name", e.target.value)}
                  placeholder="Enter project name"
                  required
                />
              </div>

              {/* Quote Due Date, Date Quoted, and Status */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)' }}>
                <div>
                  <Input
                    label="Quote Due Date"
                    type="date"
                    value={formData.quote_due_date}
                    onChange={(e) => handleInputChange("quote_due_date", e.target.value)}
                  />
                </div>
                <div>
                  <Input
                    label="Date Quoted"
                    type="date"
                    value={formData.date_quoted}
                    onChange={(e) => handleInputChange("date_quoted", e.target.value)}
                  />
                </div>
                <div>
                  <Dropdown
                    label="Quote Status"
                    options={quoteStatusOptions}
                    value={formData.quote_status}
                    onChange={(value) => handleInputChange("quote_status", value)}
                    placeholder="Select status..."
                  />
                </div>
              </div>
            </div>

            {/* Client and Contact Section */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Client Information
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                {/* Form Company Name (readonly if no client selected) */}
                {!formData.selectedClientId && (
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                      }}
                    >
                      Form Company Name
                    </label>
                    <div
                      style={{
                        padding: 'var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                      }}
                    >
                      {formData.company_name || "Not provided"}
                    </div>
                  </div>
                )}

                {/* Client Dropdown */}
                <div style={{ gridColumn: formData.selectedClientId ? '1 / -1' : 'auto' }}>
                  <Dropdown
                    label="Client"
                    options={clientOptions}
                    value={formData.selectedClientId}
                    onChange={(value) => handleInputChange("selectedClientId", value)}
                    placeholder="Search for client..."
                    searchable
                  />
                </div>

                {/* Form Contact Name (readonly if no contact selected) */}
                {!formData.selectedContactId && (
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: 'var(--gray-300)',
                      }}
                    >
                      Form Contact Name
                    </label>
                    <div
                      style={{
                        padding: 'var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                      }}
                    >
                      {formData.name || "Not provided"}
                    </div>
                  </div>
                )}

                {/* Contact Dropdown */}
                <div style={{ gridColumn: formData.selectedContactId ? '1 / -1' : 'auto' }}>
                  <Dropdown
                    label="Project Contact"
                    options={contactOptions}
                    value={formData.selectedContactId}
                    onChange={(value) => handleInputChange("selectedContactId", value)}
                    placeholder={formData.selectedClientId ? "Select contact..." : "Select client first..."}
                    disabled={!formData.selectedClientId}
                  />
                </div>
              </div>
            </div>

            {/* File Attachments */}
            {quote?.file_attachments && quote.file_attachments.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                      color: 'var(--gray-300)',
                    }}
                  >
                    File Attachments ({quote.file_attachments.length})
                  </label>
                  <button
                    type="button"
                    onClick={handleToggleCopySection}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-1) var(--space-3)',
                      backgroundColor: showCopySection ? 'var(--gray-700)' : 'var(--dark-elevated)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--cta-blue)',
                      fontSize: 'var(--text-xs)',
                      cursor: 'pointer',
                      transition: 'all var(--transition-fast)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--gray-700)';
                      e.currentTarget.style.borderColor = 'var(--cta-blue)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = showCopySection ? 'var(--gray-700)' : 'var(--dark-elevated)';
                      e.currentTarget.style.borderColor = 'var(--gray-600)';
                    }}
                  >
                    <Copy size={12} />
                    Copy to Project
                    {showCopySection ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>

                {/* File list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {quote.file_attachments.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: 'var(--space-2) var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: `1px solid ${showCopySection && selectedFilesToCopy[index] ? 'var(--cta-blue)' : 'var(--gray-700)'}`,
                        borderRadius: 'var(--radius-md)',
                        transition: 'all 0.15s ease',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        if (showCopySection) {
                          toggleFileSelection(index);
                        } else {
                          handleFileDownload(
                            file.url || file.path || file,
                            file.filename || file.name || `Attachment ${index + 1}`
                          );
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = showCopySection && selectedFilesToCopy[index] ? 'var(--cta-blue)' : 'var(--gray-600)';
                        e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = showCopySection && selectedFilesToCopy[index] ? 'var(--cta-blue)' : 'var(--gray-700)';
                        e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                      }}
                    >
                      {showCopySection ? (
                        <div
                          style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: 'var(--radius-sm)',
                            border: `2px solid ${selectedFilesToCopy[index] ? 'var(--cta-blue)' : 'var(--gray-500)'}`,
                            backgroundColor: selectedFilesToCopy[index] ? 'var(--cta-blue)' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: 'var(--space-2)',
                            flexShrink: 0,
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {selectedFilesToCopy[index] && <Check size={12} color="white" />}
                        </div>
                      ) : (
                        <Download size={14} style={{ marginRight: 'var(--space-2)', color: 'var(--cta-blue)', flexShrink: 0 }} />
                      )}
                      <span style={{
                        fontSize: 'var(--text-sm)',
                        color: 'var(--gray-200)',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {file.filename || file.name || `Attachment ${index + 1}`}
                      </span>
                      {showCopySection && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileDownload(
                              file.url || file.path || file,
                              file.filename || file.name || `Attachment ${index + 1}`
                            );
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 'var(--space-1)',
                            cursor: 'pointer',
                            color: 'var(--gray-400)',
                            display: 'flex',
                            flexShrink: 0,
                          }}
                          title="Download file"
                        >
                          <Download size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Select All toggle when in copy mode */}
                {showCopySection && (
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    style={{
                      marginTop: 'var(--space-2)',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      color: 'var(--cta-blue)',
                      fontSize: 'var(--text-xs)',
                    }}
                  >
                    {Object.values(selectedFilesToCopy).every(v => v) ? 'Deselect All' : 'Select All'}
                  </button>
                )}

                {/* Copy to Project Section */}
                {showCopySection && (
                  <div
                    style={{
                      marginTop: 'var(--space-4)',
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-bg)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <h3 style={{
                      margin: 0,
                      marginBottom: 'var(--space-3)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-200)',
                    }}>
                      Copy {selectedFileCount} file{selectedFileCount !== 1 ? 's' : ''} to Project
                    </h3>

                    {/* Project dropdown */}
                    {loadingProjects ? (
                      <div style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)', padding: 'var(--space-2)' }}>
                        Loading projects...
                      </div>
                    ) : clientProjects.length === 0 ? (
                      <div style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)', padding: 'var(--space-2)' }}>
                        No projects found for this client.
                      </div>
                    ) : (
                      <Dropdown
                        label="Target Project"
                        options={clientProjects.map(p => ({
                          value: p.id,
                          label: `${p.job_number}${p.rev > 0 ? ` Rev${p.rev}` : ''} - ${p.name}`,
                        }))}
                        value={selectedProjectId}
                        onChange={handleProjectSelect}
                        placeholder="Select a project..."
                        searchable
                      />
                    )}

                    {/* Target project's existing files */}
                    {selectedProjectId && (
                      <div style={{ marginTop: 'var(--space-3)' }}>
                        {loadingProjectFiles ? (
                          <div style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>
                            Loading project files...
                          </div>
                        ) : selectedProjectFiles.length > 0 ? (
                          <div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-2)',
                              marginBottom: 'var(--space-2)',
                            }}>
                              <AlertCircle size={14} style={{ color: 'var(--warning, #f59e0b)' }} />
                              <span style={{
                                fontSize: 'var(--text-xs)',
                                color: 'var(--warning, #f59e0b)',
                                fontWeight: 'var(--font-medium)',
                              }}>
                                This project already has {selectedProjectFiles.length} file{selectedProjectFiles.length !== 1 ? 's' : ''}:
                              </span>
                            </div>
                            <div style={{
                              maxHeight: '120px',
                              overflowY: 'auto',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 'var(--space-1)',
                              padding: 'var(--space-2)',
                              backgroundColor: 'var(--dark-elevated)',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--gray-700)',
                            }}>
                              {selectedProjectFiles.map((file, index) => (
                                <div
                                  key={index}
                                  style={{
                                    fontSize: 'var(--text-xs)',
                                    color: 'var(--gray-400)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                  }}
                                >
                                  <FileText size={10} style={{ flexShrink: 0 }} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {file.filename || file.name || file.original_filename || `File ${index + 1}`}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <div style={{
                              fontSize: 'var(--text-xs)',
                              color: 'var(--gray-400)',
                              marginTop: 'var(--space-2)',
                            }}>
                              Selected files will be added alongside existing files.
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            fontSize: 'var(--text-xs)',
                            color: 'var(--gray-400)',
                          }}>
                            This project has no existing file attachments.
                          </div>
                        )}

                        {/* Copy button */}
                        <div style={{ marginTop: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleCopyFiles}
                            disabled={copying || selectedFileCount === 0}
                          >
                            <Copy size={14} style={{ marginRight: 'var(--space-2)' }} />
                            {copying ? 'Copying...' : `Copy ${selectedFileCount} File${selectedFileCount !== 1 ? 's' : ''}`}
                          </Button>
                          {copySuccess && (
                            <span style={{
                              fontSize: 'var(--text-xs)',
                              color: 'var(--success, #22c55e)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-1)',
                            }}>
                              <Check size={12} />
                              {copySuccess}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Additional Features (Readonly) */}
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)',
                }}
              >
                Additional Features
              </label>
              <div
                style={{
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--gray-200)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                {Array.isArray(formData.additional_requests) && formData.additional_requests.length > 0
                  ? formData.additional_requests.join(", ")
                  : "None selected"}
              </div>
            </div>

            {/* Additional Notes (Readonly) */}
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)',
                }}
              >
                Additional Notes
              </label>
              <div
                style={{
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-700)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--gray-200)',
                  fontSize: 'var(--text-sm)',
                  minHeight: '80px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {formData.project_info
                  ? formatTextWithLinks(formData.project_info)
                  : "No additional information provided"}
              </div>
            </div>

            {/* Notes from AB Civil */}
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)',
                }}
              >
                Notes from AB Civil
              </label>
              <textarea
                value={formData.notes_from_ab_civil}
                onChange={(e) => handleInputChange("notes_from_ab_civil", e.target.value)}
                placeholder="Enter notes from AB Civil"
                rows={3}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--dark-surface)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--gray-200)',
                  fontSize: 'var(--text-sm)',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            {/* Left section - Delete button */}
            <div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
              >
                <Trash2 size={14} style={{ marginRight: 'var(--space-2)' }} />
                Delete
              </Button>
            </div>

            {/* Center section - Empty */}
            <div></div>

            {/* Right section - Save buttons */}
            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={saving || deleting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || deleting}
              >
                <Save size={16} style={{ marginRight: 'var(--space-2)' }} />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
              {formData.quote_status !== "Quoted" && (
                <Button
                  variant="cta"
                  onClick={handleSaveAndSend}
                  disabled={saving || deleting}
                >
                  <Send size={16} style={{ marginRight: 'var(--space-2)' }} />
                  {saving ? "Sending..." : "Save and Send"}
                </Button>
              )}
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Model Quote"
        message={`Are you sure you want to delete the quote for "${formData.project_name || 'this project'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
      />
    </>
  );
};

export default ModelQuotesModalNoPricing;
